/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods */
import EmberObject from '@ember/object';

import formulas from '../utils/formulas';
import { eng3 } from '@ebiz/pretty-num';

////////////////////////////////////////////////////////////////////////////////////
// circuitData.js: Model structure of the internal circuitry of the selected      //
// Instrumentation Amplifier                                                      //
////////////////////////////////////////////////////////////////////////////////////

export default class CircuitData extends EmberObject {
  vOutCir = null;
  negVout = null;
  posVout = null;
  negVin = null;
  posVin = null;
  negVinGain = null;
  posVinGain = null;
  negVinGain2Amp = null;
  vdiffCurrent = null;
  vbf = null;
  subtractIn = null;
  preAmpOutPos = null;
  preAmpOutNeg = null;
  gainAmpPos = null;
  gainAmpNeg = null;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  init() {
    super.init(...arguments);
    this.vOutCir = this.vOutCir || { display: null, val: null };
    this.negVout = this.negVout || { display: null, val: null };
    this.posVout = this.posVout || { display: null, val: null };
    this.negVin = this.negVin || { display: null, val: null };
    this.posVin = this.posVin || { display: null, val: null };
    this.negVinGain = this.negVinGain || { display: null, val: null };
    this.posVinGain = this.posVinGain || { display: null, val: null };
    this.negVinGain2Amp = this.negVinGain2Amp || { display: null, val: null };
    this.vdiffCurrent = this.vdiffCurrent || { display: null, val: null };
    this.vbf = this.vbf || { display: null, val: null };
    this.subtractIn = this.subtractIn || { display: null, val: null };
    this.preAmpOutPos = this.preAmpOutPos || { display: null, val: null };
    this.preAmpOutNeg = this.preAmpOutNeg || { display: null, val: null };
    this.gainAmpPos = this.gainAmpPos || { display: null, val: null };
    this.gainAmpNeg = this.gainAmpNeg || { display: null, val: null };
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Model Data Building Functions                                              //
  ////////////////////////////////////////////////////////////////////////////////

  buildCircuit(s, pD, type, vref) {
    var varT = Number(pD.varTypeSlider),
      vDiff = Number(pD.vDiffSlider),
      gain = Number(s.selectedGainInput.id),
      preGain = Math.max(gain, 1),
      gda = Math.min(gain, 1);

    this.negVin.val = Math.round(this.calculateNegVin(vDiff, varT, type) * 10000) / 10000;
    this.posVin.val = Math.round(this.calculatePosVin(vDiff, varT, type) * 10000) / 10000;
    this.vOutCir.val = Math.round(this.calculateVOutCir(vDiff, gain, vref) * 10000) / 10000;
    this.negVout.val = Math.round(this.calculateNegVout(vDiff, gain, vref) * 10000) / 10000;
    this.posVout.val = Math.round(this.calculatePosVout(vDiff, gain, vref) * 10000) / 10000;
    this.negVinGain.val = Math.round(this.calculateNegVinGain(vDiff, varT, type, s, gain) * 10000) / 10000;
    this.posVinGain.val = Math.round(this.calculatePosVinGain(vDiff, varT, type, s, gain) * 10000) / 10000;
    this.negVinGain2Amp.val =
      Math.round(this.calculateNegVinGain2Amp(vDiff, varT, type, s, gain, vref) * 10000) / 10000;
    this.vdiffCurrent.val = Math.round(this.calculateVdiffCurrent(vDiff, varT, type) * 10000) / 10000;
    this.vbf.val = Math.round(this.calculateVbf(vDiff, gain, vref) * 10000) / 10000;
    this.subtractIn.val = Math.round(this.calculateSubtractIn(vDiff, varT, type, s, gain, vref) * 10000) / 10000;
    this.preAmpOutPos.val =
      Math.round(this.calculatePreAmpOutPos(preGain, this.posVin.val, this.negVin.val) * 10000) / 10000;
    this.preAmpOutNeg.val =
      Math.round(this.calculatePreAmpOutNeg(preGain, this.posVin.val, this.negVin.val) * 10000) / 10000;
    this.gainAmpPos.val =
      Math.round(this.calculateGainAmpPos(gda, this.posVout.val, this.preAmpOutPos.val) * 10000) / 10000;
    this.gainAmpNeg.val =
      Math.round(this.calculateGainAmpNeg(gda, this.negVout.val, this.preAmpOutNeg.val) * 10000) / 10000;

    this.setProperties({
      'negVin.display': eng3(this.negVin.val),
      'posVin.display': eng3(this.posVin.val),
      'vOutCir.display': eng3(this.vOutCir.val),
      'negVout.display': eng3(this.negVout.val),
      'posVout.display': eng3(this.posVout.val),
      'negVinGain.display': eng3(this.negVinGain.val),
      'posVinGain.display': eng3(this.posVinGain.val),
      'negVinGain2Amp.display': eng3(this.negVinGain2Amp.val),
      'vdiffCurrent.display': eng3(this.vdiffCurrent.val),
      'vbf.display': eng3(this.vbf.val),
      'subtractIn.display': eng3(this.subtractIn.val),
      'preAmpOutPos.display': eng3(this.preAmpOutPos.val),
      'preAmpOutNeg.display': eng3(this.preAmpOutNeg.val),
      'gainAmpPos.display': eng3(this.gainAmpPos.val),
      'gainAmpNeg.display': eng3(this.gainAmpNeg.val),
    });
  }

  calculateVOutCir(vDiff, gain, vref) {
    return vDiff * gain + Number(vref);
  }

  calculateNegVout(vDiff, gain, vref) {
    return vref - (vDiff * gain) / 2;
  }

  calculatePosVout(vDiff, gain, vref) {
    return vref + (vDiff * gain) / 2;
  }

  calculateNegVin(vDiff, varT, type) {
    return type == 1
      ? Number(varT) - Number(vDiff) / 2
      : type == 2
        ? varT
        : type == 3
          ? Number(varT) - Number(vDiff)
          : 0;
  }

  calculatePosVin(vDiff, varT, type) {
    return type == 1
      ? Number(varT) + Number(vDiff) / 2
      : type == 2
        ? Number(varT) + Number(vDiff)
        : type == 3
          ? varT
          : 0;
  }

  calculateNegVinGain(vDiff, varT, type, s, gain) {
    return formulas.negVinGain(
      type == 1
        ? Number(varT)
        : Number(formulas.vcm(this.calculateNegVin(vDiff, varT, type), this.calculatePosVin(vDiff, varT, type))),
      s.vdir,
      Number(gain),
      s.gda,
      Number(vDiff),
    );
  }

  calculatePosVinGain(vDiff, varT, type, s, gain) {
    return formulas.posVinGain(
      type == 1
        ? Number(varT)
        : Number(formulas.vcm(this.calculateNegVin(vDiff, varT, type), this.calculatePosVin(vDiff, varT, type))),
      s.vdir,
      Number(gain),
      s.gda,
      Number(vDiff),
    );
  }

  calculateNegVinGain2Amp(vDiff, varT, type, s, gain, vref) {
    let go = gain;
    if (s.diagramType == 'PINSTRAPPED') {
      // specific logic for LTC 2amp parts
      go = gain;
    } else {
      go = s.gda;
    }
    return formulas.negVinGain2Amp(
      type == 1
        ? Number(varT)
        : Number(formulas.vcm(this.calculateNegVin(vDiff, varT, type), this.calculatePosVin(vDiff, varT, type))),
      s.vdir,
      Number(gain),
      go,
      Number(this.calculatePosVin(vDiff, varT, type) - this.calculateNegVin(vDiff, varT, type)),
      Number(vref),
    );
  }

  calculateVdiffCurrent(vDiff, varT, type) {
    return Number(this.calculatePosVin(vDiff, varT, type) - this.calculateNegVin(vDiff, varT, type));
  }

  calculateVbf(vDiff, gain, vref) {
    return (this.calculateVOutCir(vDiff, gain, vref) - vref) / gain + vref;
  }

  calculateSubtractIn(vDiff, varT, type, s, gain, vref) {
    return (this.calculatePosVinGain(vDiff, varT, type, s, gain) - vref) * (s.gda / (s.gda + 1)) + vref;
  }

  calculatePreAmpOutPos(preGain, posVin, negVin) {
    let vdiff = posVin - negVin;
    let vcm = vdiff / 2 + negVin;
    return vcm - (preGain * vdiff) / 2;
  }

  calculatePreAmpOutNeg(preGain, posVin, negVin) {
    let vdiff = posVin - negVin;
    let vcm = vdiff / 2 + negVin;
    return vcm + (preGain * vdiff) / 2;
  }

  calculateGainAmpPos(gda, posVout, preAmpOutPos) {
    return this.calculateGainAmp(gda, posVout, preAmpOutPos);
  }

  calculateGainAmpNeg(gda, negVout, preAmpOutNeg) {
    return this.calculateGainAmp(gda, negVout, preAmpOutNeg);
  }

  calculateGainAmp(gda, vout, preAmpOut) {
    return vout / (gda + 1) + preAmpOut / (1 + 1 / gda);
  }
}
